import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddCajaMutation } from "@/features/cajas/caja-list/api/add-caja";
import { DeleteCajaMutation } from "@/features/cajas/caja-list/api/delete-caja";
import { EditCajaMutation } from "@/features/cajas/caja-list/api/edit-caja";
import { getAllCajas, useGetAllCajasQuery } from "@/features/cajas/caja-list/api/get-all-cajas";
import { GetCajaQuery } from "@/features/cajas/caja-list/api/get-caja";
import CajaForm, { cajaFormSchema } from "@/features/cajas/caja-list/components/caja-form";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";

export default function CajasPage() {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteCajas);

    const filterData = useFilters({
        filters: [
            { type: "string", label: "Nombre", name: "nombre" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllCajasQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "nombre", header: "Nombre", render: row => row.nombre },
            { key: "abierto", header: "Abierto", render: row => row.abierto ? "Abierto" : "Cerrado" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "cajas"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Cajas">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Caja</ModalHeader>
            <AddCajaMutation>
                {mutation => <SchemaFormHook
                    schema={cajaFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("La caja se ha agregado correctamente.");
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <CajaForm form={form} margin={3} />}
                </SchemaFormHook>}
            </AddCajaMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Caja</ModalHeader>
            <ModalValueExtractor>
                {cajaId => <GetCajaQuery id={cajaId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {caja => <EditCajaMutation id={caja.id}>
                            {mutation => <SchemaFormHook
                                schema={cajaFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify("La caja se ha editado correctamente.");
                                }}
                                defaultValues={{ ...caja }}
                                submitOnce
                                closeModalOnSubmit
                            >
                                {form => <CajaForm form={form} margin={3} />}
                            </SchemaFormHook>}
                        </EditCajaMutation>}
                    </DataFetcher>}
                </GetCajaQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {caja => <DeleteCajaMutation id={caja.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Caja "${caja.nombre}"`}
                        description={`¿Estás seguro que quieres borrar la caja "${caja.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`La caja "${caja.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteCajaMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajas}
            defaultFileTitle="Cajas"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajas}
            defaultFileTitle="Cajas"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajas}
            defaultFileTitle="Cajas"
        />
    </ListPage>
}